import React, { useEffect, useMemo, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import "../assets/css/Projects.css";
import { getAuthItem } from "../utils/authStorage";

const STATUS_OPTIONS = ["Active", "Expired", "Cancelled"];

function EditProjectSubscription({ onBack, subscriptionId: subscriptionIdProp }) {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const subscriptionId = subscriptionIdProp ?? idParam;

  const role = useMemo(() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);

  const canEdit = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.EDIT, role);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  const [projectId, setProjectId] = useState(null);
  const [formData, setFormData] = useState({
    planId: "1",
    amount: "",
    startDate: "",
    endDate: "",
    status: "Active"
  });

  const getUpdatedBy = () => {
    try {
      const u = getAuthItem("user");
      if (!u) return 1;
      const user = JSON.parse(u);
      return user.user_id ?? user.id ?? user.User_Id ?? 1;
    } catch {
      return 1;
    }
  };

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingPlans(true);
      const fallback = [{ id: 1, name: "Monthly" }];
      try {
        const res = await axiosInstance.get("/fico/subscription-plans");
        const arr = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
        const mapped = (arr || [])
          .map((p) => {
            const pid = Number(p?.Plan_Id ?? p?.plan_id ?? p?.id);
            const name = String(p?.Plan_Name ?? p?.plan_name ?? p?.name ?? "").trim();
            if (!pid || Number.isNaN(pid)) return null;
            return { id: pid, name: name || `Plan ${pid}` };
          })
          .filter(Boolean);
        setPlans(mapped.length > 0 ? mapped : fallback);
      } catch (err) {
        console.error("Error fetching subscription plans:", err);
        setPlans(fallback);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!subscriptionId) return;
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/fico/project-subscription/${subscriptionId}`);
        const s = res?.data?.data ?? res?.data ?? null;
        setProjectId(s?.Project_Id ?? s?.project_id ?? null);

        setFormData({
          planId: String(s?.Plan_Id ?? s?.plan_id ?? "1"),
          amount: s?.Amount ?? s?.amount ?? "",
          startDate: String(s?.Start_Date ?? s?.start_date ?? "").slice(0, 10),
          endDate: String(s?.End_Date ?? s?.end_date ?? "").slice(0, 10),
          status: s?.Status ?? s?.status ?? "Active"
        });
      } catch (err) {
        console.error("Error loading subscription:", err);
        alert("Failed to load subscription details.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [subscriptionId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subscriptionId) return;
    if (!canEdit) {
      alert("You don't have permission to edit project subscriptions.");
      return;
    }
    if (!projectId) {
      alert("Project Id is missing for this subscription.");
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      alert("Start Date and End Date are required.");
      return;
    }
    if (formData.endDate < formData.startDate) {
      alert("End Date cannot be before Start Date.");
      return;
    }

    const planIdNum = Number(formData.planId);
    if (!planIdNum || Number.isNaN(planIdNum)) {
      alert("Please select a plan.");
      return;
    }

    const amountStr = String(formData.amount ?? "").trim();
    const amountNum = amountStr === "" ? null : Number(amountStr.replace(/,/g, ""));
    if (amountNum != null && (Number.isNaN(amountNum) || amountNum < 0)) {
      alert("Please enter a valid amount (0 or greater).");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        Project_Id: Number(projectId),
        Plan_Id: planIdNum,
        Amount: amountStr === "" ? null : String(amountStr),
        Start_Date: formData.startDate,
        End_Date: formData.endDate,
        Status: formData.status,
        Updated_By: getUpdatedBy()
      };

      let res = null;
      try {
        res = await axiosInstance.put(`/fico/update-project-subscription/${subscriptionId}`, payload);
      } catch {
        // Some deployments may implement this as POST
        res = await axiosInstance.post(`/fico/update-project-subscription/${subscriptionId}`, payload);
      }

      const refresh = Date.now();
      alert(res?.data?.message || "Subscription updated successfully.");
      navigate(`/projects/${projectId}/view?refresh=${refresh}`);
    } catch (err) {
      console.error("Error updating subscription:", err);
      alert(err.response?.data?.message || err.response?.data?.error || err.message || "Failed to update subscription.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div>Loading subscription...</div>
      </div>
    );
  }

  return (
    <div className="viewlead-container">
      <div className="viewlead-header">
        <button className="viewlead-back-link" type="button" onClick={onBack || (() => navigate(-1))}>
          <FaArrowLeft /> Back
        </button>
      </div>

      <div className="add-project-payment-form">
        <h4 className="viewlead-section-title" style={{ marginBottom: 16 }}>
          Edit Subscription
        </h4>

        <form onSubmit={handleSubmit} className="project-payment-form-grid">
          <div className="form-group">
            <label>Plan</label>
            <select
              name="planId"
              value={formData.planId}
              onChange={handleChange}
              disabled={loadingPlans || submitting}
            >
              <option value="">{loadingPlans ? "Loading..." : "Select plan"}</option>
              {plans.map((pl) => (
                <option key={pl.id} value={String(pl.id)}>
                  {pl.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Amount</label>
            <input
              type="text"
              name="amount"
              inputMode="decimal"
              pattern="^[0-9]*[.,]?[0-9]*$"
              value={formData.amount}
              onChange={handleChange}
              disabled={submitting}
              placeholder="e.g. 15000"
            />
          </div>

          <div className="form-group">
            <label>Start Date</label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              min={today}
              disabled={submitting}
              required
            />
          </div>

          <div className="form-group">
            <label>End Date</label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              min={formData.startDate || today}
              disabled={submitting}
              required
            />
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              disabled={submitting}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="form-group form-group-actions">
            <button type="submit" className="btn-subproject-primary" disabled={submitting || !canEdit}>
              {submitting ? "Updating..." : "Update Subscription"}
            </button>
            <button type="button" className="btn-secondary" onClick={onBack || (() => navigate(-1))} disabled={submitting}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProjectSubscription;

